/**
 * Web FCM wiring (SIG-08) — closes the residual freshness gap where an OPEN web tab wouldn't see a new
 * Daily Signal (the note websocket doesn't carry signals). This is the highest-reuse option: the entire
 * server push pipeline (device-token store, `/wake` hop, FCM fan-out) already reaches any token in the
 * store, and `platform:'web'` + `registerDevice` already exist. The only new pieces are minting a web
 * token (a service worker + `getToken` with a VAPID key) and turning a received message into a refetch.
 *
 * Everything here is best-effort and heavily guarded: native shells (Tauri handle their own FCM),
 * browsers without service-worker/Notification support, a missing VAPID key, or a denied permission all
 * fall back cleanly to the focus/visibility refetch from SIG-05.
 */
import { getApp, getApps } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { isTauri } from './shell/shell.js';
import { getOrCreateDeviceId } from './deviceSync.js';
import { dispatchSignalWake } from './signalsWake.js';
import type { FirebaseWebConfig } from './auth/firebaseConfig.js';
import type { ApiClient } from './auth/apiClient.js';

/** Build the SW registration URL carrying the (public) Firebase config as query params. */
export function swRegistrationUrl(config: FirebaseWebConfig, path = '/firebase-messaging-sw.js'): string {
  const q = new URLSearchParams();
  q.set('apiKey', config.apiKey);
  q.set('authDomain', config.authDomain);
  q.set('projectId', config.projectId);
  q.set('appId', config.appId);
  if (config.messagingSenderId) q.set('messagingSenderId', config.messagingSenderId);
  return `${path}?${q.toString()}`;
}

let started = false;

/** Whether web push can even be attempted in this environment (before any permission prompt). */
export function canUseWebPush(config: FirebaseWebConfig | null): config is FirebaseWebConfig {
  if (!config?.vapidKey) return false;
  if (typeof window === 'undefined' || isTauri()) return false;
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return false;
  if (!getApps().length) return false; // Firebase app not initialized (no auth config)
  return true;
}

/**
 * Register this web browser for signal wakes. Idempotent (runs once per load). Returns silently on any
 * unsupported/denied/errored path so it never disrupts the app.
 */
export async function initWebPush(api: ApiClient, config: FirebaseWebConfig | null): Promise<void> {
  if (started || !canUseWebPush(config)) return;
  if (!(await isSupported().catch(() => false))) return;
  // Ask once; if not granted we simply never mint a token (the focus refetch still covers freshness).
  let permission = Notification.permission;
  if (permission === 'default') permission = await Notification.requestPermission();
  if (permission !== 'granted') return;
  started = true;
  try {
    const registration = await navigator.serviceWorker.register(swRegistrationUrl(config));
    const messaging = getMessaging(getApp());
    const token = await getToken(messaging, { vapidKey: config.vapidKey, serviceWorkerRegistration: registration });
    if (token) await api.registerDevice(getOrCreateDeviceId(), token, 'web');
    // Foreground delivery (the focused-tab case this phase exists for) — silent refetch, no notification.
    onMessage(messaging, (payload) => dispatchSignalWake(payload.data?.noteId));
    // Backgrounded-but-open tab: the SW postMessages us to refetch.
    navigator.serviceWorker.addEventListener('message', (e: MessageEvent) => {
      const data = e.data as { type?: string; noteId?: string } | undefined;
      if (data?.type === 'lapo-signals-wake') dispatchSignalWake(data.noteId);
    });
  } catch {
    started = false; // allow a retry on the next sign-in
  }
}
